import { useEffect, useRef, useState } from 'react';

const defaultOptions = {
  rootMargin: '0px 0px -60px 0px',
  threshold: 0.1,
  triggerOnce: true,
};

/**
 * Hook to reveal an element when it scrolls into view (for CSS animations).
 * @param {Object} options - IntersectionObserver options + triggerOnce
 * @returns {[React.RefObject, boolean]} - ref to attach to element, and isVisible
 */
export function useScrollReveal(options = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const opts = { ...defaultOptions, ...options };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (opts.triggerOnce && ref.current) {
              observer.unobserve(ref.current);
            }
          } else if (!opts.triggerOnce) {
            setIsVisible(false);
          }
        });
      },
      { rootMargin: opts.rootMargin, threshold: opts.threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [opts.rootMargin, opts.threshold, opts.triggerOnce]);

  return [ref, isVisible];
}
